import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SecurityGuard from "../../guards/SecurityGuard";
import { useGuardRequest } from "../../context/useGuardRequest";
import Dialog from "../../library/Dialog";
import * as adminSubscriptionServices from "../../../contexts/services/admin/adminSubscriptionServices";

const cronJobs = [
  {
    key: "audit-cleanup",
    actionKey: "runAuditCleanupCron",
    title: "Audit cleanup",
    description:
      "Delete audit logs older than the configured retention period.",
    icon: CleaningServicesOutlinedIcon,
    request: adminSubscriptionServices.runAuditCleanup,
  },
  {
    key: "billing-engine",
    actionKey: "runBillingEngineCron",
    title: "Billing engine",
    description:
      "Process due subscription billing and update billing statuses.",
    icon: PaymentsOutlinedIcon,
    request: adminSubscriptionServices.runBillingEngine,
  },
  {
    key: "company-cleanup",
    actionKey: "runCompanyCleanupCron",
    title: "Company cleanup",
    description:
      "Permanently remove deleted companies once their retention period has passed.",
    icon: CorporateFareOutlinedIcon,
    request: adminSubscriptionServices.runCompanyCleanup,
  },
];

const getActionErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const CronJobsConsole = () => {
  const [cronRunning, setCronRunning] = useState(null);
  const [snack, setSnack] = useState(null);

  const { guardProps, runActionWithMiddleware } = useGuardRequest({
    policy: {
      runAuditCleanupCron: true,
      runBillingEngineCron: true,
      runCompanyCleanupCron: true,
    },
  });

  const runCronJob = async (job) => {
    if (cronRunning || guardProps.open) return;

    await runActionWithMiddleware({
      actionKey: job.actionKey,
      details: (
        <Typography variant='body2' color='text.secondary'>
          Run {job.title} cron job now.
        </Typography>
      ),
      execute: async () => {
        setCronRunning(job.key);
        try {
          const response = await job.request();
          const message =
            response?.data?.message ||
            response?.data?.data?.message ||
            `${job.title} completed.`;
          setSnack({ message, title: job.title });
        } catch (error) {
          setSnack({
            message: getActionErrorMessage(
              error,
              `${job.title} could not be run.`
            ),
            severity: "error",
            title: job.title,
          });
        } finally {
          setCronRunning(null);
        }
      },
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, lg: 3 },
        minWidth: 0,
        "& .MuiButton-root": { textTransform: "none" },
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Avatar
            variant='rounded'
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <ScheduleOutlinedIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component='h1' variant='h5' fontWeight={750}>
              Cron Jobs
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Run billing and maintenance tasks when needed.
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.025),
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='flex-start'>
            <InfoOutlinedIcon
              sx={{ color: "primary.main", fontSize: 20, mt: 0.25 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='body2' fontWeight={700}>
                Applies across the platform
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                These tasks can affect all organizations. Choose a quiet period
                to run them.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Box component='section' aria-labelledby='cron-tasks-title'>
          <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 0.5 }}>
            <Typography
              id='cron-tasks-title'
              component='h2'
              variant='subtitle1'
              fontWeight={700}
            >
              Maintenance tasks
            </Typography>
            <Chip
              size='small'
              label={cronJobs.length + " tasks"}
              sx={{ height: 24, fontWeight: 600 }}
            />
          </Stack>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Choose a task, then complete security verification to start it.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {cronJobs.map((job) => {
              const Icon = job.icon;
              const isRunning = cronRunning === job.key;
              return (
                <Paper
                  key={job.key}
                  component='article'
                  aria-labelledby={job.key + "-title"}
                  aria-busy={isRunning}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    border: 1,
                    borderColor: isRunning ? "primary.main" : "divider",
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 3px 16px rgba(30,20,50,0.025)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, flex: 1 }}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      gap={1}
                    >
                      <Avatar
                        variant='rounded'
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                          color: "primary.main",
                        }}
                      >
                        <Icon fontSize='small' />
                      </Avatar>
                      <Box role='status'>
                        {isRunning && (
                          <Chip
                            label='Running'
                            size='small'
                            variant='outlined'
                            color='primary'
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Stack>
                    <Box>
                      <Typography
                        id={job.key + "-title"}
                        component='h3'
                        variant='subtitle1'
                        fontWeight={700}
                      >
                        {job.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5, lineHeight: 1.6 }}
                      >
                        {job.description}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      px: { xs: 2, sm: 2.5 },
                      py: 1.5,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Button
                      fullWidth
                      variant='outlined'
                      disabled={Boolean(cronRunning) || guardProps.open}
                      onClick={() => runCronJob(job)}
                      startIcon={
                        isRunning ? (
                          <CircularProgress size={16} color='inherit' />
                        ) : (
                          <PlayArrowRoundedIcon />
                        )
                      }
                      sx={{ minHeight: 38, fontWeight: 600, borderRadius: 1.5 }}
                    >
                      {isRunning ? "Running..." : "Run now"}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Stack>

      <SecurityGuard {...guardProps} />

      <Dialog
        open={Boolean(snack)}
        onClose={() => setSnack(null)}
        title={snack?.title || "Task result"}
        subtitle={
          snack?.severity === "error"
            ? "Could not complete this task"
            : undefined
        }
        content={snack?.message || ""}
        confirmText='OK'
        cancelText='Close'
        onConfirm={() => setSnack(null)}
        maxWidth='xs'
        fullScreen={false}
        paperSx={{ borderRadius: 3, border: 1, borderColor: "divider" }}
        headerSx={{ p: 2.5 }}
        contentSx={{ p: 2.5, overflowWrap: "anywhere" }}
        actionsSx={{ px: 2.5, py: 1.5 }}
        confirmButtonSx={{
          boxShadow: "none",
          borderRadius: 1.5,
          textTransform: "none",
        }}
      />
    </Box>
  );
};

export default CronJobsConsole;
